import { useProfileContext } from '../profile.context';
import { useStates } from '@uplift/queries';
import { AboutMeSection, ExpertiseSection, ExpertiseSectionProps, MoreInfoSection, ProviderHeader, SelectedProviderContext, SelectedProviderSubSectionEnum, convertPublicProfileToSelectedProviderType } from '@uplift/pages';
import { Box, Divider, Drawer, Toolbar, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { MatchingProfileFueStep, matchingProfileFueSteps } from './ProfileFueSteps.component';
import { ScrollableView, useAlert } from '@uplift/ui-v2';
import { useCallback, useEffect, useMemo } from 'react';
import { ProviderMatchingProfileBookmark } from '@uplift/api';
import { useIsMobile } from '@uplift/hooks';


export type PreviewDrawerProps = {
  open: boolean;
  currentStep: MatchingProfileFueStep;
  status: MatchingProfileFueStep;
  enableTransitionDelay?: boolean;
};

export const PreviewDrawer: React.VFC<PreviewDrawerProps> = props => {
  const isMobile = useIsMobile('md');
  const { classes, cx } = usePreviewDrawerStyles();
  const { profile } = useProfileContext();
  const { states } = useStates();
  const alert = useAlert();

  const onPreviewDrawerClick = () => {
    alert.info(
      <Box width="436px">
        <Typography variant="body2" color="textPrimary">
          Trying to edit this section? You can edit it once you've filled out the other sections—or by using the <b>Back</b> button if you want to make changes now.
        </Typography>
      </Box>,
      { showIcon: false }
    );
  };

  const scrollToSection = useCallback(async (step: MatchingProfileFueStep, smooth = true, timeout?: number) => {
    const sectionId = getSectionId(step);

    if (sectionId) {
      const element = document.getElementById(sectionId);

      if (element) {

        if (timeout) {
          await new Promise(resolve => setTimeout(resolve, timeout));
        }

        element.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant', block: 'center' });
      }
    }
  }, []);

  useEffect(() => {
    scrollToSection(props.currentStep, true, props.enableTransitionDelay ? 500 : undefined);
  }, [ scrollToSection, props.currentStep, props.enableTransitionDelay ]);

  useEffect(() => {
    scrollToSection(props.currentStep, false);
  }, []);

  const transitionConfig = useMemo((): ExpertiseSectionProps['fieldTransitions'] => {
    const statusIndex = matchingProfileFueSteps.indexOf(props.status);

    return matchingProfileFueSteps.reduce((acc: ExpertiseSectionProps['fieldTransitions'], step, currentIndex) => {
      const isHightlighted = step === props.currentStep;
      const isShown = currentIndex <= statusIndex;
      const transitionIn = currentIndex <= statusIndex - 1;

      return { ...acc, [step]: { shown: isShown, transitionIn, active: isHightlighted } };
    }, {});
  }, [ props.currentStep, props.status ]);

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={props.open}
      className={cx(classes.drawer, { [classes.drawerOpened]: props.open && !isMobile })}
      classes={{
        paper: classes.drawerPaper,
      }}
      onClick={onPreviewDrawerClick}
    >
      <Toolbar />
      <ScrollableView>
        <SelectedProviderContext.Provider
          value={{
            selectedProvider: convertPublicProfileToSelectedProviderType(profile, states),
            matchedInsuranceCompany: null,
          }}
        >
          <Box className={classes.previewContent}>
            <Box p={2}>
              {/* use first license state for preview */}
              <ProviderHeader stateId={profile.licenses?.[0]?.stateId} />
            </Box>
            <Divider />
            <Box className={classes.sectionsContainer}>
              <ExpertiseSection fieldTransitions={transitionConfig} forceMobile />
              <AboutMeSection fieldTransitions={transitionConfig} forceMobile />
              <MoreInfoSection fieldTransitions={transitionConfig} forceMobile hideLicenses />
            </Box>
            <Box p={2} textAlign="center">
              <Typography variant="body2">Preview only. You can make any changes at the end.</Typography>
            </Box>
          </Box>
        </SelectedProviderContext.Provider>
      </ScrollableView>
    </Drawer>
  );
};

const getSectionId = (step: ProviderMatchingProfileBookmark): SelectedProviderSubSectionEnum => {
  switch (step) {
    case ProviderMatchingProfileBookmark.supportedAgeGroups:
    case ProviderMatchingProfileBookmark.modalities:
      return SelectedProviderSubSectionEnum.workWithTheseClients;
    case ProviderMatchingProfileBookmark.specialties:
      return SelectedProviderSubSectionEnum.specialties;
    case ProviderMatchingProfileBookmark.treatmentApproaches:
      return SelectedProviderSubSectionEnum.treatmentApproach;
    case ProviderMatchingProfileBookmark.communityExpertise:
      return SelectedProviderSubSectionEnum.communityExpertise;
    case ProviderMatchingProfileBookmark.bio:
      return SelectedProviderSubSectionEnum.bio;
    case ProviderMatchingProfileBookmark.languages:
      return SelectedProviderSubSectionEnum.spokenLanguages;
    case ProviderMatchingProfileBookmark.faiths:
    case ProviderMatchingProfileBookmark.ethnicities:
    case ProviderMatchingProfileBookmark.gender:
      return SelectedProviderSubSectionEnum.identifiesAs;
    case ProviderMatchingProfileBookmark.pronouns:
    case ProviderMatchingProfileBookmark.photo:
      return SelectedProviderSubSectionEnum.header;
    case ProviderMatchingProfileBookmark.therapeuticStyles:
      return SelectedProviderSubSectionEnum.therapeuticStyle;
    case ProviderMatchingProfileBookmark.qualification:
      return SelectedProviderSubSectionEnum.education;
    case ProviderMatchingProfileBookmark.locations:
      return SelectedProviderSubSectionEnum.practiceAddress;
    default:
      return null;
  }
};

const PREVIEW_DRAWER_WIDTH = 500;
const usePreviewDrawerStyles = makeStyles({ name: 'PreviewDrawer' })(theme => ({
  drawer: {
    zIndex: 999,
    width: '0px',
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerOpened: {
    width: PREVIEW_DRAWER_WIDTH,
    maxWidth: '90%',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaper: {
    width: PREVIEW_DRAWER_WIDTH,
    maxWidth: '90%',
    boxShadow: theme.shadows[4],

    [theme.breakpoints.up('lg')]: {
      boxShadow: 'none',
    },
  },
  previewContent: {
    paddingBottom: theme.spacing(30),
  },
  sectionsContainer: {
    background: theme.palette.grey[50],
    display: 'flex',
    flexDirection: 'column',
    gridGap: '2.5rem',
    paddingTop: theme.spacing(3),
    flex: 1,
  },
}));
